import { useCallback, useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, imgSrc, money } from "@/lib/api";
import { Field, FormDialog, RowActions, field } from "./FormBits";
import { ImageUpload } from "./ImageUpload";
import { FlyerQuickUpload } from "./FlyerQuickUpload";

const EMPTY = { title: "", date: "", time: "9PM", venue: "", city: "", region: "West Coast", price: 25, vip_price: "", ticket_url: "", status: "on_sale", lineup: "", image_url: "" };
const TEXT = [["title", "Title"], ["date", "Date", "date"], ["time", "Doors"], ["venue", "Venue"], ["city", "City"], ["region", "Region (filter tab)"], ["price", "General admission price", "number"], ["vip_price", "VIP ticket price (blank = no VIP tickets)", "number"], ["ticket_url", "Ticket link"], ["lineup", "Lineup"]];

export const ShowsTab = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const load = useCallback(() => { api.get("/admin/shows").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const { id, ...body } = form;
    body.price = Number(body.price); body.vip_price = body.vip_price === "" || body.vip_price == null ? null : Number(body.vip_price);
    try {
      if (id) await api.put(`/admin/shows/${id}`, body); else await api.post("/admin/shows", body);
      toast.success("Show saved"); setForm(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    await api.delete(`/admin/shows/${s.id}`).catch(() => {}); toast.success("Show deleted"); load();
  };

  return (
    <div>
      <button data-testid="admin-add-show-btn" onClick={() => setForm({ ...EMPTY })} className="mb-4 inline-flex items-center gap-2 bg-[#56ED4A] text-black px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] font-semibold"><Plus size={14} /> Add show</button>
      <div className="flex flex-col gap-2">
        {rows.map((s) => (
          <div key={s.id} data-testid={`admin-show-${s.id}`} className="border border-white/10 bg-[#121216] p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex gap-3 items-center">
            {s.image_url && <img src={imgSrc(s.image_url)} alt="" className="w-14 h-[70px] object-cover border border-white/10" />}
            <div>
              <div className="font-mono text-xs text-[#56ED4A]">{s.date} · {s.city}</div>
              <div className="font-display uppercase text-2xl">{s.title}</div>
              <div className="font-mono text-xs text-zinc-400">{s.venue} · GA {money(s.price)}{s.vip_price != null ? ` / VIP ${money(s.vip_price)}` : ""} · {s.status}</div>
            </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <FlyerQuickUpload show={s} onDone={load} />
              <RowActions testId={`admin-show-${s.id}`} onEdit={() => setForm({ ...s, vip_price: s.vip_price ?? "" })} onDelete={() => del(s)} />
            </div>
          </div>
        ))}
      </div>
      {form && (
        <FormDialog open onOpenChange={(o) => !o && setForm(null)} title={form.id ? "Edit show" : "New show"} onSubmit={save} testId="show-form">
          <div className="sm:col-span-2"><Field label="Show flyer (gig poster)"><ImageUpload value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} testId="show-form-flyer" label="flyer" /></Field></div>
          {TEXT.map(([k, l, t]) => (
            <Field key={k} label={l}>
              <input data-testid={`show-form-${k}`} type={t || "text"} step="0.01" required={["title", "date", "venue", "city"].includes(k)} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={field} />
            </Field>
          ))}
          <Field label="Status">
            <select data-testid="show-form-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={`${field} bg-[#121216]`}>
              <option value="on_sale">On sale</option><option value="selling_fast">Selling fast</option><option value="sold_out">Sold out</option>
            </select>
          </Field>
        </FormDialog>
      )}
    </div>
  );
};
